import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useFormik } from "formik";
import Drawer from "../../../../../components/SideDrawer/SideDrawer";
import { useDebouncedCallback } from "../../../../../hooks/useDebounce";
import CloseIcon from "../../../../../assets/svg/close-icon";
import CircularLoader from "../../../../../components/Loader/CircularLoader";
import CheckedIcon from "../../../../../assets/svg/checked-icon";
import Input from "../../../../../components/Input/Input";
import PhoneNumberCountryCodeSelector from "../../../../../components/PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import {
  transformCountryOptions,
  sortCountryOptions,
  formatPhoneNumber,
  isEmptyPhoneNumber,
} from "../../../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { countryOptions } from "../../../../../utils/constants/countryOptions";
import { CountryOption } from "../../../../../components/PhoneNumberCountryCodeSelector/utils/types";
import Button from "../../../../../components/Button/Button";
import SearchableDropdown from "../../../../../components/SearchableDropdown/SearchableDropdown";
import {
  useGetLeadCountriesQuery,
  useGetLeadCitiesQuery,
  useGetLeadUniversitiesQuery,
  useGetUtmSourcesQuery,
  useGetUtmMediumQuery,
  useGetLeadDetailTagsQuery,
  useVerifyLeadExistsMutation,
} from "../../../../../redux/services/communicationApi";
import { useGetPropertiesListQuery } from "../../../../../redux/services/playgroundApi";
import LeadExistsWarning from "./LeadExistsWarning";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetLeadStageFieldOptionsQuery,
  useGetAllLeadStagesQuery,
  useGetLeadCreateDefaultsQuery,
} from "../../../../../redux/services/crmApi";
import { useGetAllAgentsQuery } from "../../../../../redux/services/userManagementApi";
import { roomTypeOptions } from "../../../../../utils/constants/roomTypeOptions";
import Dropdown from "../../../../../components/Dropdown/Dropdown";
import { useTenantTypeOptions } from "../../../../../hooks/useTenantTypeOptions";
import MultiSelectSearchableDropdown from "../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import { getTagBgColor } from "../../../utils/functions";
import {
  getFilteredLevelOfStudyOptions,
  shouldShowLevelOfStudy,
} from "../../../utils/createEditLeadFormConfig";
import { currencyOptions } from "../../../../../utils/constants/globalConstants";
import SingleDatePicker from "../../../../../components/SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import { LeadDetails } from "../../../utils/types";
import { visibleScrollbar } from "../../../../../utils/functions/get-visible-scrollbar";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import {
  getDefaultCurrencySymbol,
  getDefaultDurationUnit,
  getLeadTagDisplayName,
} from "../../../../../utils/constants/mappings";
import { FORM_FIELDS_CONFIG } from "../utils/constants";
import {
  getSortedFields,
  mapFieldKeyToFormikName,
  createDynamicValidationSchema,
  transformLeadDetailsToFormValues,
  isFieldRequired,
} from "../utils/functions";
import {
  UtmSource,
  UtmMedium,
  LeadDetailTag,
  FormValues,
  Country,
  City,
  University,
  FieldConfig,
  DropdownOption,
  ExtendedLeadFormValues,
  VerificationResult,
} from "../utils/types";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";
import AddUserIcon from "../../../../../assets/svg/add-user-icon";
import EditUserIcon from "../../../../../assets/svg/edit-user-icon";

interface LeadVerificationProps {
  emailVerification: VerificationResult;
  phoneVerification: VerificationResult;
  whatsappVerification: VerificationResult;
  setEmailVerification: React.Dispatch<React.SetStateAction<VerificationResult>>;
  setPhoneVerification: React.Dispatch<React.SetStateAction<VerificationResult>>;
  setWhatsappVerification: React.Dispatch<React.SetStateAction<VerificationResult>>;
  verifyEmailOnChange: (email: string) => void;
  verifyPhoneOnChange: (digitsOnly: string, countryCode?: string) => void;
  verifyWhatsappOnChange: (digitsOnly: string, countryCode?: string) => void;
  handleUseDifferentField: (type: "email" | "phone" | "whatsapp") => void;
  handleViewLead: (leadId: string) => void;
}

interface DynamicFieldRendererProps {
  field: FieldConfig;
  formik: ReturnType<typeof useFormik<FormValues>>;
  getOptions: (fieldKey: string) => DropdownOption[];
  getLoadingState: (fieldKey: string) => boolean;
  getPhoneHandlers: () => {
    phoneInput: string;
    whatsappInput: string;
    sortedCountryOptions: CountryOption[];
    handlePhoneCountryChange: (country: CountryOption) => void;
    handleWhatsappCountryChange: (country: CountryOption) => void;
    handlePhoneInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleWhatsappInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSameAsPhoneNumberChange: () => void;
  };
  getBudgetConfig: () => {
    leadCurrencyUnit?: string;
    leadDurationUnit?: string;
    clientRegion?: string;
  };
  hasPhoneField: boolean;
  verification: LeadVerificationProps;
}

// Renders a field label, appending a red "*" when the field is required.
// Single source of truth so every input type shows the indicator consistently.
const renderRequiredLabel = (
  displayName: string,
  required: boolean
): React.ReactNode =>
  required ? (
    <>
      {displayName}
      <span className="text-verba-red-alert dark:text-verba-red-alert-dk ml-[2px]">
        *
      </span>
    </>
  ) : (
    displayName
  );

const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  field,
  formik,
  getOptions,
  getLoadingState,
  getPhoneHandlers,
  getBudgetConfig,
  hasPhoneField,
  verification,
}) => {
  const formikFieldName = mapFieldKeyToFormikName(
    field.field_key
  ) as keyof FormValues;
  const value = formik.values[formikFieldName];
  const error =
    formik.touched[formikFieldName] && formik.errors[formikFieldName];
  const budgetConfig = getBudgetConfig();
  const required = isFieldRequired(field);
  const labelNode = renderRequiredLabel(field.display_name, required);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      formik.setFieldValue(formikFieldName, date);
    },
    [formik, formikFieldName]
  );

  const handleMultiSelectChange = useCallback(
    (values: string[]) => {
      formik.setFieldValue(formikFieldName, values);
    },
    [formik, formikFieldName]
  );

  const handleSingleValueChange = useCallback(
    (val: string | null) => {
      formik.setFieldValue(formikFieldName, val);
    },
    [formik, formikFieldName]
  );

  const handleDestinationCountryChange = useCallback(
    (val: string | null) => {
      formik.setFieldValue(formikFieldName, val);
      if (formik.values.destinationCity) {
        formik.setFieldValue("destinationCity", null);
      }
      if (formik.values.university) {
        formik.setFieldValue("university", null);
      }
    },
    [formik, formikFieldName]
  );

  const handleDestinationCityChange = useCallback(
    (val: string | null) => {
      formik.setFieldValue(formikFieldName, val);
      if (formik.values.university) {
        formik.setFieldValue("university", null);
      }
    },
    [formik, formikFieldName]
  );

  const handleTenantTypeChange = useCallback(
    (val: string | null) => {
      formik.setFieldValue(formikFieldName, val);
      if (formik.values.levelOfStudy) {
        formik.setFieldValue("levelOfStudy", null);
      }
    },
    [formik, formikFieldName]
  );

  const handleUtmSourceChange = useCallback(
    (value: string | null) => {
      // setFieldValue validates against the NEW value; mark touched WITHOUT a
      // second validation (shouldValidate=false) so it can't re-check the stale
      // (pre-update) value and flash a spurious "required" error on select.
      formik.setFieldValue("utmSource", value);
      formik.setFieldTouched("utmSource", true, false);
    },
    [formik]
  );

  const handleTagChange = useCallback(
    (values: string[]) => {
      formik.setFieldValue("tags", values);
      formik.setFieldTouched("tags", true, false);
    },
    [formik]
  );

  // "How did you hear about us" is a long enum list served in the field config's
  // `possible_values` (from /settings/lead-form/form). Handle it up-front, by
  // field_key, so it always renders as a searchable dropdown regardless of the
  // backend's input_type / option_source (e.g. "both" would otherwise skip the
  // static options in the generic dropdown_single branch).
  if (field.field_key === "how_did_you_hear_about_us") {
    const options: DropdownOption[] = (field.possible_values || []).map(
      (val) => ({
        value: val,
        content: (
          <div className="flex items-center">
            <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              {val}
            </span>
          </div>
        ),
        label: val,
      })
    );
    return (
      <SearchableDropdown
        id={`${field.field_key}-dropdown`}
        label={labelNode}
        labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
        options={options}
        value={value as string | null}
        onChange={handleSingleValueChange}
        placeholder={field?.placeholder || "Select how did you hear about us"}
        searchPlaceholder="Search..."
        sectionHeading="All Options"
        showSearch={true}
        className="w-full"
        selectedOptionContainerClassName="h-[36px] w-full"
        // dropdownClassName="!top-auto !bottom-full !mt-0 mb-1"
        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
        dropdownMenuClassName="!max-h-[150px]"
      />
    );
  }

  // Handle phone fields
  if (field.input_type === "phone") {
    const phoneHandlers = getPhoneHandlers();

    if (field.field_key === "phone") {
      return (
        <div className="flex flex-col gap-[6px] w-full">
          <PhoneNumberCountryCodeSelector
            label={labelNode}
            labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
            placeholder={field?.placeholder || "Enter phone number"}
            countryOptions={phoneHandlers.sortedCountryOptions}
            selectedCountry={formik.values.phoneCountry}
            onCountryChange={(country) => {
              phoneHandlers.handlePhoneCountryChange(country);
              const digits = phoneHandlers.phoneInput.replace(/\D/g, "");
              if (digits.length >= 7) {
                verification.verifyPhoneOnChange(digits, country.dialCode);
              }
            }}
            phoneInput={phoneHandlers.phoneInput}
            onPhoneInputChange={(e) => {
              phoneHandlers.handlePhoneInputChange(e);
              const digits = e.target.value.replace(/\D/g, "");
              verification.verifyPhoneOnChange(digits);
            }}
            validationError={error ? String(error) : ""}
            containerClassName="w-full"
            countryDropdownClassName="w-[250px] min-w-[250px] max-h-[250px]"
            countryDropdownSelectedOptionClassName="h-[34px] px-[8px] py-[9px]"
            phoneInputClassName={`h-[36px] px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border ${
              verification.phoneVerification.status === "exists"
                ? "border-verba-red-alert dark:border-verba-red-alert-dk shadow-[2px_2px_5px_rgba(225,63,65,0.1)]"
                : "border-border-gray dark:border-border-gray-dk"
            }`}
            countryDropdownPlaceholder="Select Country"
            countryDropdownSearchPlaceholder="Search countries..."
            countryDropdownSectionHeading="All Countries"
          />
          {verification.phoneVerification.status === "loading" && (
            <div className="flex gap-[4px] items-center">
              <CircularLoader size={14} className="text-verba-blue dark:text-verba-blue-dk" />
              <span className="text-[11px] leading-normal text-verba-blue dark:text-verba-blue-dk">
                Checking if this number is already in your CRM…
              </span>
            </div>
          )}
          {verification.phoneVerification.status === "clear" && (
            <div className="flex gap-[4px] items-center">
              <CheckedIcon className="w-[14px] h-[14px] text-green-500" />
              <span className="text-[11px] leading-normal text-green-500">
                Not linked to any existing lead
              </span>
            </div>
          )}
          {verification.phoneVerification.status === "exists" &&
            verification.phoneVerification.leadData && (
              <LeadExistsWarning
                leadData={verification.phoneVerification.leadData}
                fieldType="phone"
                onViewLead={verification.handleViewLead}
                onUseDifferent={() => verification.handleUseDifferentField("phone")}
              />
            )}
        </div>
      );
    } else if (field.field_key === "whatsapp_phone") {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-[6px] w-full">
            <PhoneNumberCountryCodeSelector
              label={labelNode}
              labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
              placeholder={field?.placeholder || "Enter whatsapp number"}
              countryOptions={phoneHandlers.sortedCountryOptions}
              selectedCountry={formik.values.whatsappCountry}
              disabled={hasPhoneField && formik.values.isSameAsPhone}
              onCountryChange={(country) => {
                phoneHandlers.handleWhatsappCountryChange(country);
                const digits = phoneHandlers.whatsappInput.replace(/\D/g, "");
                if (digits.length >= 7) {
                  verification.verifyWhatsappOnChange(digits, country.dialCode);
                }
              }}
              phoneInput={phoneHandlers.whatsappInput}
              onPhoneInputChange={(e) => {
                phoneHandlers.handleWhatsappInputChange(e);
                const digits = e.target.value.replace(/\D/g, "");
                verification.verifyWhatsappOnChange(digits);
              }}
              validationError={error ? String(error) : ""}
              containerClassName="w-full"
              countryDropdownClassName="w-[250px] min-w-[250px] max-h-[250px]"
              countryDropdownSelectedOptionClassName="h-[34px] px-[8px] py-[9px]"
              phoneInputClassName={`h-[36px] px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border ${
                verification.whatsappVerification.status === "exists"
                  ? "border-verba-red-alert dark:border-verba-red-alert-dk shadow-[2px_2px_5px_rgba(225,63,65,0.1)]"
                  : "border-border-gray dark:border-border-gray-dk"
              }`}
              countryDropdownPlaceholder="Select Country"
              countryDropdownSearchPlaceholder="Search countries..."
              countryDropdownSectionHeading="All Countries"
            />
            {verification.whatsappVerification.status === "loading" && (
              <div className="flex gap-[4px] items-center">
                <CircularLoader size={14} className="text-verba-blue dark:text-verba-blue-dk" />
                <span className="text-[11px] leading-normal text-verba-blue dark:text-verba-blue-dk">
                  Checking if this number is already in your CRM…
                </span>
              </div>
            )}
            {verification.whatsappVerification.status === "clear" && (
              <div className="flex gap-[4px] items-center">
                <CheckedIcon className="w-[14px] h-[14px] text-green-500" />
                <span className="text-[11px] leading-normal text-green-500">
                  Not linked to any existing lead
                </span>
              </div>
            )}
            {verification.whatsappVerification.status === "exists" &&
              verification.whatsappVerification.leadData && (
                <LeadExistsWarning
                  leadData={verification.whatsappVerification.leadData}
                  fieldType="whatsapp"
                  onViewLead={verification.handleViewLead}
                  onUseDifferent={() => verification.handleUseDifferentField("whatsapp")}
                />
              )}
          </div>
          {hasPhoneField && (
            <label className="flex items-center gap-[5px] cursor-pointer ml-1 w-max">
              <input
                type="checkbox"
                className="w-3 h-3 rounded-[3px] cursor-pointer"
                checked={formik.values.isSameAsPhone}
                onChange={phoneHandlers.handleSameAsPhoneNumberChange}
              />
              <span className="text-gray-placeholder dark:text-gray-placeholder-dk font-normal text-xs select-none cursor-pointer">
                Same as Phone Number
              </span>
            </label>
          )}
        </div>
      );
    }
  }

  // Handle date picker
  if (field.input_type === "date_picker") {
    return (
      <div className="flex flex-col gap-1">
        <SingleDatePicker
          label={labelNode}
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          placeholder={field?.placeholder || "Select Date"}
          Icon={CalendarIcon}
          value={value as Date | null}
          onChange={handleDateChange}
          disablePastDates={true}
          scrollIntoView={false}
          // lockBodyScroll={false}
          containerClassName="w-full"
          iconClassName="w-[20px] h-[20px] text-gray-placeholder dark:text-gray-placeholder-dk"
          inputClassName={`w-full h-full text-md font-normal px-[10px] py-[7px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk`}
        />
        {error && (
          <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
            {String(error)}
          </span>
        )}
      </div>
    );
  }

  if (field.input_type === "searchable_dropdown_single") {
    // Dynamic options
    let options: DropdownOption[] = [];
    let onChange = handleSingleValueChange;
    let disabled = false;
    const isLoading = getLoadingState(field.field_key);
    let placeholder =
      field?.placeholder || `Select ${field.display_name.toLowerCase()}`;

    if (field.field_key === "demographics.destination_country") {
      options = getOptions(field.field_key);
      onChange = handleDestinationCountryChange;
    } else if (field.field_key === "demographics.destination_city") {
      options = getOptions(field.field_key);
      disabled = !formik.values.destinationCountry;
      placeholder = !formik.values.destinationCountry
        ? "Select a country first"
        : field?.placeholder || `Select ${field.display_name.toLowerCase()}`;
      onChange = handleDestinationCityChange;
    } else if (field.field_key === "demographics.university") {
      options = getOptions(field.field_key);
      disabled =
        !formik.values.destinationCountry || !formik.values.destinationCity;
      placeholder = !formik.values.destinationCountry
        ? "Select a country first"
        : !formik.values.destinationCity
          ? "Select a city first"
          : field?.placeholder || `Select ${field.display_name.toLowerCase()}`;
    } else if (field.field_key === "demographics.source_country") {
      // Source country is a top-level field (no parent dependency), so it must
      // never gate on its own value — that self-referential guard locked it
      // permanently disabled. Leave it enabled with the default placeholder.
      options = getOptions(field.field_key);
    } else if (field.field_key === "utm_source") {
      options = getOptions(field.field_key);
      onChange = handleUtmSourceChange;
      placeholder = field?.placeholder || "Select a UTM source";
    } else if (field.field_key === "utm_medium") {
      options = getOptions(field.field_key);
      placeholder = field?.placeholder || "Select UTM medium";
    } else if (field.field_key === "current_stage") {
      options = getOptions(field.field_key);
    }
    return (
      <SearchableDropdown
        id={`${field.field_key}-dropdown`}
        label={labelNode}
        labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
        options={options}
        value={value as string | null}
        onChange={onChange}
        placeholder={placeholder || field?.placeholder || ""}
        searchPlaceholder={`Search ${field.display_name.toLowerCase()}...`}
        sectionHeading={`All ${field.display_name}`}
        showSearch={true}
        className="w-full"
        selectedOptionContainerClassName="h-[36px] w-full"
        loadingText={
          isLoading ? `Loading ${field.display_name.toLowerCase()}...` : ""
        }
        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
        dropdownMenuClassName="!max-h-[150px]"
        disabled={disabled}
        error={
          !!(formik.touched[formikFieldName] && formik.errors[formikFieldName])
        }
        errorMessage={
          formik.touched[formikFieldName] &&
          formik.errors[formikFieldName as keyof FormValues]
            ? String(formik.errors[formikFieldName as keyof FormValues])
            : ""
        }
      />
    );
  }

  // Handle dropdown single
  if (field.input_type === "dropdown_single") {
    let options: DropdownOption[] = [];
    const placeholder =
      field?.placeholder || `Select ${field.display_name.toLowerCase()}`;
    let onChange = handleSingleValueChange;

    if (field.option_source === "static" && field.possible_values) {
      options = field.possible_values.map((val) => ({
        value: val,
        content: (
          <div className="flex items-center">
            <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
              {val.replace(/_/g, " ")}
            </span>
          </div>
        ),
        label: val.replace(/_/g, " "),
      }));
    }

    // Special handling for tenant_type
    if (field.field_key === "tenant_type") {
      options = getOptions(field.field_key);
      onChange = handleTenantTypeChange;
    }
    // Special handling for level_of_study
    else if (field.field_key === "level_of_study") {
      options = getOptions(field.field_key);
      if (!shouldShowLevelOfStudy(formik.values.tenantType)) {
        return null;
      }
    }
    return (
      <div className="flex flex-col gap-1">
        <Dropdown
          id={`${field.field_key}-dropdown`}
          label={labelNode}
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          options={options}
          value={value as string | null}
          onChange={onChange}
          placeholder={placeholder}
          ariaLabel={
            field?.placeholder || `Select ${field.display_name.toLowerCase()}`
          }
          className="w-full"
          selectedOptionContainerClassName="h-[36px] w-full"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          dropdownClassName="!max-h-[150px] mt-1"
        />
        {error && (
          <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
            {String(error)}
          </span>
        )}
      </div>
    );
  }

  // Handle dropdown multi
  if (field.input_type === "dropdown_multi") {
    const options = getOptions(field.field_key);
    const isLoading = getLoadingState(field.field_key);

    return (
      <div className="flex flex-col gap-1">
        <MultiSelectSearchableDropdown
          id={`${field.field_key}-dropdown`}
          label={labelNode}
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          options={options}
          value={(value as string[]) || []}
          onChange={handleMultiSelectChange}
          placeholder={
            field?.placeholder || `Select ${field.display_name.toLowerCase()}`
          }
          searchPlaceholder={`Search ${field.display_name.toLowerCase()}...`}
          maxDisplayedSelections={2}
          showSearch={true}
          loadingText={
            isLoading ? `Loading ${field.display_name.toLowerCase()}...` : ""
          }
          disabled={isLoading}
          className="w-full gap-1!"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          triggerClassName="h-[36px]"
          dropdownOpenClassName="!max-h-[150px]"
        />
        {error && (
          <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
            {String(error)}
          </span>
        )}
      </div>
    );
  }

  // Handle tag dropdown
  if (field.input_type === "tag_dropdown") {
    const isLoading = getLoadingState(field.field_key);

    return (
      <div className="flex flex-col gap-1">
        <MultiSelectSearchableDropdown
          id={`${field.field_key}-dropdown`}
          label={labelNode}
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          options={getOptions(field.field_key)}
          value={(value as string[]) || []}
          onChange={handleTagChange}
          placeholder={
            field.placeholder || `Select ${field.display_name.toLowerCase()}`
          }
          searchPlaceholder={`Search ${field.display_name.toLowerCase()}...`}
          maxDisplayedSelections={1}
          showSearch={true}
          loadingText={
            isLoading ? `Loading ${field.display_name.toLowerCase()}...` : ""
          }
          disabled={isLoading}
          className="w-full"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          triggerClassName="h-[36px]"
          dropdownOpenClassName="!max-h-[150px]"
        />
        {error && (
          <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
            {String(error)}
          </span>
        )}
      </div>
    );
  }

  // Handle budget input - render as two separate fields (min_budget and max_budget)
  //move budget config to globval level
  if (field.input_type === "budget") {
    return (
      <>
        <div className="flex flex-col gap-1">
          <p className="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md">
            {renderRequiredLabel("Min Budget", required)}
          </p>
          <div className="flex items-center gap-2 h-[36px] px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk">
            <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              {budgetConfig.leadCurrencyUnit
                ? currencyOptions[
                    budgetConfig.leadCurrencyUnit as keyof typeof currencyOptions
                  ]
                : getDefaultCurrencySymbol(budgetConfig.clientRegion)}
            </span>
            <Input
              id="min_budget"
              name="min_budget"
              type="text"
              placeholder="Min"
              value={formik.values.min_budget}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              containerClassName="w-full"
              secondaryContainerClassName="w-full"
            />
            <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
              /
              {budgetConfig.leadDurationUnit ||
                getDefaultDurationUnit(budgetConfig.clientRegion)}
            </span>
          </div>
          {formik.touched.min_budget && formik.errors.min_budget && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk mt-1">
              {String(formik.errors.min_budget)}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md">
            {renderRequiredLabel("Max Budget", required)}
          </p>
          <div className="flex items-center gap-2 h-[36px] px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk">
            <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              {budgetConfig.leadCurrencyUnit
                ? currencyOptions[
                    budgetConfig.leadCurrencyUnit as keyof typeof currencyOptions
                  ]
                : getDefaultCurrencySymbol(budgetConfig.clientRegion)}
            </span>
            <Input
              id="max_budget"
              name="max_budget"
              type="text"
              placeholder="Max"
              value={formik.values.max_budget}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              containerClassName="w-full"
              secondaryContainerClassName="w-full"
            />
            <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
              /
              {budgetConfig.leadDurationUnit ||
                getDefaultDurationUnit(budgetConfig.clientRegion)}
            </span>
          </div>
          {formik.touched.max_budget && formik.errors.max_budget && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk mt-1">
              {String(formik.errors.max_budget)}
            </span>
          )}
        </div>
      </>
    );
  }

  // Handle text, email, number inputs
  if (field.input_type === "text" || field.input_type === "email") {
    let inputType = "text";
    if (field.field_data_type === "number") {
      inputType = "number";
    } else if (field.field_data_type === "email") {
      inputType = "email";
    }

    // Special rendering for stay_duration with duration unit
    if (field.field_key === "stay_duration") {
      return (
        <div className="flex flex-col gap-1">
          <p className="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md">
            {labelNode}
          </p>
          <div className="flex items-center gap-2 h-[36px] px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk">
            <Input
              id={formikFieldName}
              name={formikFieldName}
              type={inputType}
              placeholder={field?.placeholder || field.display_name}
              value={(value as string) || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              containerClassName="w-full"
              secondaryContainerClassName="w-full"
            />
            <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
              /
              {budgetConfig.leadDurationUnit ||
                getDefaultDurationUnit(budgetConfig.clientRegion) ||
                ""}
            </span>
          </div>
          {formik.touched[formikFieldName] &&
            formik.errors[formikFieldName] && (
              <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk mt-1">
                {String(formik.errors[formikFieldName])}
              </span>
            )}
        </div>
      );
    }

    // Email field with verification
    if (field.field_data_type === "email") {
      return (
        <div className="flex flex-col gap-[6px] w-full">
          <Input
            label={labelNode}
            name={formikFieldName}
            id={formikFieldName}
            type={inputType}
            placeholder={
              field?.placeholder || `Enter ${field.display_name.toLowerCase()}`
            }
            value={(value as string) || ""}
            onChange={(e) => {
              formik.handleChange(e);
              verification.verifyEmailOnChange(e.target.value);
            }}
            onBlur={formik.handleBlur}
            error={!!error || verification.emailVerification.status === "exists"}
            errorMessage={error ? String(error) : ""}
            showLoader={verification.emailVerification.status === "loading"}
            loaderClassName="text-verba-blue dark:text-verba-blue-dk"
            containerClassName="w-full"
            labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
            secondaryContainerClassName={`h-[36px] px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border ${
              verification.emailVerification.status === "exists"
                ? "border-verba-red-alert dark:border-verba-red-alert-dk shadow-[2px_2px_5px_rgba(225,63,65,0.1)]"
                : "border-border-gray dark:border-border-gray-dk"
            }`}
          />
          {verification.emailVerification.status === "loading" && (
            <div className="flex gap-[4px] items-center">
              <CircularLoader size={14} className="text-verba-blue dark:text-verba-blue-dk" />
              <span className="text-[11px] leading-normal text-verba-blue dark:text-verba-blue-dk">
                Checking if this email is already in your CRM…
              </span>
            </div>
          )}
          {verification.emailVerification.status === "clear" && (
            <div className="flex gap-[4px] items-center">
              <CheckedIcon className="w-[14px] h-[14px] text-green-500" />
              <span className="text-[11px] leading-normal text-green-500">
                Not linked to any existing lead
              </span>
            </div>
          )}
          {verification.emailVerification.status === "exists" &&
            verification.emailVerification.leadData && (
              <LeadExistsWarning
                leadData={verification.emailVerification.leadData}
                fieldType="email"
                onViewLead={verification.handleViewLead}
                onUseDifferent={() => verification.handleUseDifferentField("email")}
              />
            )}
        </div>
      );
    }

    // Regular text/number inputs
    return (
      <Input
        label={labelNode}
        name={formikFieldName}
        id={formikFieldName}
        type={inputType}
        placeholder={
          field?.placeholder || `Enter ${field.display_name.toLowerCase()}`
        }
        value={(value as string) || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={!!error}
        errorMessage={error ? String(error) : ""}
        containerClassName="w-full"
        labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
        secondaryContainerClassName="h-[36px] px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
      />
    );
  }

  return null;
};

const DynamicLeadCreateEditForm = ({
  openDrawer,
  handleCloseDrawer,
  size,
  isEdit = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isCampaign = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isDowning = false,
  isVfAdmin = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validations = [],
  leadCurrencyUnit,
  leadDurationUnit,
  leadDetails = null,
  onSubmit,
  isUpdating = false,
  viewId,
  skipCurrentStage = false,
}: {
  openDrawer: boolean;
  handleCloseDrawer: () => void;
  size: string;
  isEdit?: boolean;
  isCampaign?: boolean;
  isDowning?: boolean;
  isVfAdmin?: boolean;
  validations?: string[];
  leadCurrencyUnit?: string;
  leadDurationUnit?: string;
  leadDetails?: LeadDetails | null;
  onSubmit?: (
    values: ExtendedLeadFormValues,
    prevLeadDetails?: LeadDetails | null
  ) => void;
  isUpdating?: boolean;
  viewId?: string;
  skipCurrentStage?: boolean;
}) => {
  const transformedCountryOptions = transformCountryOptions(countryOptions);
  const sortedCountryOptions = sortCountryOptions(transformedCountryOptions);

  const client = useSelector((state: RootState) => state?.auth?.client);
  const leadFormFieldsConfig = useSelector(
    (state: RootState) => state?.leads?.leadsFormConfig
  );

  // Get fields sorted by display order
  const sortedFields: FieldConfig[] = useMemo(
    () =>
      getSortedFields(
        (leadFormFieldsConfig as FieldConfig[]) ||
          FORM_FIELDS_CONFIG.settings.fields
      ),
    [leadFormFieldsConfig]
  );

  const validationSchema = useMemo(
    () => createDynamicValidationSchema(sortedFields, isEdit, isVfAdmin),
    [sortedFields, isEdit, isVfAdmin]
  );

  const initialValues = useMemo(
    () =>
      transformLeadDetailsToFormValues(
        leadDetails,
        transformedCountryOptions,
        client?.region
      ),
    [leadDetails, transformedCountryOptions, client?.region]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    // Validate on mount so the Save button reflects required-field state
    // before the user interacts (e.g. an untouched create form stays disabled).
    validateOnMount: true,
    onSubmit: (values) => {
      // console.log("✅ Form submitted with values:", values);

      // Get the names for the selected IDs
      const getSelectedCountryName = () => {
        if (!values.destinationCountry || !countryData) return undefined;
        const country = countryData.find(
          (c: Country) => c.id.toString() === values.destinationCountry
        );
        return country?.name;
      };

      const getSelectedCityName = () => {
        if (!values.destinationCity || !cityData) return undefined;
        const city = cityData.find(
          (c: City) => c.id.toString() === values.destinationCity
        );
        return city?.name;
      };

      const getSelectedUniversityName = () => {
        if (!values.university || !universityData) return undefined;
        const university = universityData.find(
          (u: University) => u.id.toString() === values.university
        );
        return university?.name;
      };

      // Get complete tag data for selected tags (for API payload - no colors)
      const getSelectedTagsData = () => {
        if (!values.tags?.length || !leadDetailTagsData) return [];
        return values.tags.map((code: string) => {
          const tagData = leadDetailTagsData.find(
            (tag: LeadDetailTag) => tag.code === code
          );
          if (!tagData) {
            return {
              name: "",
              label: "",
              code: code,
            };
          }
          const displayName = getLeadTagDisplayName(
            tagData.name,
            client?.client_code
          );
          return {
            name: displayName,
            label: displayName,
            code: tagData.code,
          };
        });
      };

      // Get complete tag data with colors for Redux update
      const getSelectedTagsDataWithColors = () => {
        if (!values.tags?.length || !leadDetailTagsData) return [];
        return values.tags.map((code: string) => {
          const tagData = leadDetailTagsData.find(
            (tag: LeadDetailTag) => tag.code === code
          );
          return tagData
            ? {
                name: tagData.name,
                label: tagData.name,
                code: tagData.code,
                colour: tagData.colour,
              }
            : {
                name: "",
                label: "",
                code: code,
                colour: "#FFF6E6",
              };
        });
      };

      const normalizedValues: FormValues = {
        ...values,
        phoneCountry: isEmptyPhoneNumber(values.phoneNumber)
          ? null
          : values.phoneCountry,
        phoneNumber: isEmptyPhoneNumber(values.phoneNumber)
          ? null
          : values.phoneNumber,
        whatsappCountry: isEmptyPhoneNumber(values.whatsappNumber)
          ? null
          : values.whatsappCountry,
        whatsappNumber: isEmptyPhoneNumber(values.whatsappNumber)
          ? null
          : values.whatsappNumber,
      };

      const getChoosenPropsPayload = () => {
        if (!values.choosenProps?.length) return [];
        // choosenPropsOptions also covers properties outside the fetched page
        // (the lead's existing props and the client's default property).
        return values.choosenProps.map((val: string) => {
          const option = choosenPropsOptions.find(
            (opt: DropdownOption) => opt.value === val
          );
          return {
            property_id: parseInt(val),
            property_name: option?.label || "",
          };
        });
      };

      const valuesWithNames = {
        ...normalizedValues,
        selectedTagOptions: values.tags,
        selectedTagsData: getSelectedTagsData(),
        selectedTagsDataWithColors: getSelectedTagsDataWithColors(),
        destinationCountryName: getSelectedCountryName(),
        destinationCityName: getSelectedCityName(),
        universityName: getSelectedUniversityName(),
        choosenPropsPayload: getChoosenPropsPayload(),
      };

      // console.log("Calling onSubmit with:", valuesWithNames);
      onSubmit?.(valuesWithNames, leadDetails);
    },
  });

  const [phoneInput, setPhoneInput] = useState("");
  const [whatsappInput, setWhatsappInput] = useState("");

  const [emailVerification, setEmailVerification] =
    useState<VerificationResult>({ status: "idle" });
  const [phoneVerification, setPhoneVerification] =
    useState<VerificationResult>({ status: "idle" });
  const [whatsappVerification, setWhatsappVerification] =
    useState<VerificationResult>({ status: "idle" });

  const [verifyLeadExists] = useVerifyLeadExistsMutation();
  const { data: leadStagesData } = useGetAllLeadStagesQuery({});

  const handleVerifyField = useCallback(
    async (
      type: "email" | "phone" | "whatsapp",
      params: { email?: string; phone?: string; phone_country_code?: string }
    ) => {
      const setVerification =
        type === "email"
          ? setEmailVerification
          : type === "phone"
          ? setPhoneVerification
          : setWhatsappVerification;

      setVerification({ status: "loading" });

      try {
        const response = await verifyLeadExists(params).unwrap();
        if (response?.lead_id) {
          const stageData = leadStagesData?.find(
            (s: { code: string }) => s.code === response.current_stage
          );
          setVerification({
            status: "exists",
            leadData: {
              id: response.lead_id,
              name: response.name || "",
              stage: stageData?.name || response.current_stage || "",
              stage_colour: stageData?.base_color,
              created_at: response.created_at || "",
            },
          });
        } else {
          setVerification({ status: "clear" });
        }
      } catch {
        setVerification({ status: "idle" });
      }
    },
    [verifyLeadExists, leadStagesData]
  );

  const lastVerifiedEmail = useRef("");
  const lastVerifiedPhone = useRef("");
  const lastVerifiedWhatsapp = useRef("");

  const debouncedVerifyField = useDebouncedCallback(
    (type: "email" | "phone" | "whatsapp", params: { email?: string; phone?: string; phone_country_code?: string }) => {
      handleVerifyField(type, params);
    },
    500
  );

  const verifyEmailOnChange = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailVerification({ status: "idle" });
      lastVerifiedEmail.current = "";
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(trimmed) && trimmed !== lastVerifiedEmail.current) {
      lastVerifiedEmail.current = trimmed;
      setEmailVerification({ status: "loading" });
      debouncedVerifyField("email", { email: trimmed });
    }
  };

  const verifyPhoneOnChange = (digitsOnly: string, countryCode?: string) => {
    if (!digitsOnly) {
      setPhoneVerification({ status: "idle" });
      lastVerifiedPhone.current = "";
      return;
    }
    const dialCode = countryCode || formik.values.phoneCountry?.dialCode;
    const verifyKey = `${dialCode}:${digitsOnly}`;
    if (digitsOnly.length >= 7 && verifyKey !== lastVerifiedPhone.current) {
      lastVerifiedPhone.current = verifyKey;
      setPhoneVerification({ status: "loading" });
      debouncedVerifyField("phone", { phone: digitsOnly, phone_country_code: dialCode });
    }
  };

  const verifyWhatsappOnChange = (digitsOnly: string, countryCode?: string) => {
    if (!digitsOnly) {
      setWhatsappVerification({ status: "idle" });
      lastVerifiedWhatsapp.current = "";
      return;
    }
    const dialCode = countryCode || formik.values.whatsappCountry?.dialCode;
    const verifyKey = `${dialCode}:${digitsOnly}`;
    if (digitsOnly.length >= 7 && verifyKey !== lastVerifiedWhatsapp.current) {
      lastVerifiedWhatsapp.current = verifyKey;
      setWhatsappVerification({ status: "loading" });
      debouncedVerifyField("whatsapp", { phone: digitsOnly, phone_country_code: dialCode });
    }
  };

  const handleUseDifferentField = (type: "email" | "phone" | "whatsapp") => {
    if (type === "email") {
      formik.setFieldValue("email", "");
      setEmailVerification({ status: "idle" });
      lastVerifiedEmail.current = "";
    } else if (type === "phone") {
      formik.setFieldValue("phoneNumber", "");
      setPhoneInput("");
      setPhoneVerification({ status: "idle" });
      lastVerifiedPhone.current = "";
    } else {
      formik.setFieldValue("whatsappNumber", "");
      setWhatsappInput("");
      setWhatsappVerification({ status: "idle" });
      lastVerifiedWhatsapp.current = "";
    }
  };

  const handleViewLead = (leadId: string) => {
    window.open(`/dashboard/crm/${leadId}`, "_blank");
  };

  const { data: countryData, isLoading: isCountryLoading } =
    useGetLeadCountriesQuery(openDrawer ? {} : skipToken);

  const { data: cityData, isLoading: isCityLoading } = useGetLeadCitiesQuery(
    openDrawer && formik.values.destinationCountry
      ? formik.values.destinationCountry
      : skipToken
  );

  const { data: universityData, isLoading: isUniversityLoading } =
    useGetLeadUniversitiesQuery(
      openDrawer && formik.values.destinationCity
        ? formik.values.destinationCity
        : skipToken
    );

  const { data: utmSourceData, isLoading: isUtmSourceLoading } =
    useGetUtmSourcesQuery({});

  const { data: utmMediumData, isLoading: isUtmMediumLoading } =
    useGetUtmMediumQuery({});

  const { data: propertiesData, isLoading: isChoosenPropsLoading } =
    useGetPropertiesListQuery(
      openDrawer && client?.client_code
        ? { managerId: client.client_code, page: 1, pageSize: 1000 }
        : skipToken
    );

  const choosenPropsData = useMemo(
    () => ({
      options: (propertiesData?.res || []).map(
        (property: { id: number; name: string }) => ({
          label: property.name,
          value: property.id,
        })
      ),
    }),
    [propertiesData]
  );

  const { data: leadDetailTagsData, isLoading: isLeadDetailTagsLoading } =
    useGetLeadDetailTagsQuery({});

  const { data: stageFieldOptionsData, isLoading: isStageOptionsLoading } =
    useGetLeadStageFieldOptionsQuery(
      openDrawer ? (viewId ? { view_id: viewId } : {}) : skipToken
    );

  const { data: allAgentsData, isFetching: isAgentsLoading } =
    useGetAllAgentsQuery(undefined, { skip: !openDrawer });

  // Create-lead only, and only for clients with configured defaults (aparto):
  // prefill destination country/city/property. Edit keeps the lead's own values.
  const hasLeadCreateDefaults = getClientCRMFlag(
    "hasLeadCreateDefaults",
    client?.client_code
  );
  const { data: leadCreateDefaults } = useGetLeadCreateDefaultsQuery(undefined, {
    skip: !openDrawer || isEdit || !hasLeadCreateDefaults,
  });
  const hasAppliedCreateDefaultsRef = useRef(false);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digitsOnly = inputValue.replace(/\D/g, "");
    const formattedNumber = formatPhoneNumber(
      digitsOnly,
      formik.values.phoneCountry
    );
    setPhoneInput(formattedNumber);
    formik.setFieldValue("phoneNumber", digitsOnly);
  };

  const handlePhoneCountryChange = (country: CountryOption) => {
    formik.setFieldValue("phoneCountry", country);

    if (phoneInput) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      setPhoneInput(formattedNumber);
      formik.setFieldValue("phoneNumber", digitsOnly);
    }
  };

  const handleWhatsappInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    const digitsOnly = inputValue.replace(/\D/g, "");
    const formattedNumber = formatPhoneNumber(
      digitsOnly,
      formik.values.whatsappCountry
    );
    setWhatsappInput(formattedNumber);
    formik.setFieldValue("whatsappNumber", digitsOnly);
  };

  const handleWhatsappCountryChange = (country: CountryOption) => {
    formik.setFieldValue("whatsappCountry", country);

    if (whatsappInput) {
      const digitsOnly = whatsappInput.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      setWhatsappInput(formattedNumber);
      formik.setFieldValue("whatsappNumber", digitsOnly);
    }
  };

  const handleSameAsPhoneNumberChange = () => {
    const toggledSameAsPhone = !formik?.values?.isSameAsPhone;
    formik.setFieldValue("isSameAsPhone", toggledSameAsPhone);

    if (toggledSameAsPhone) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedWhatsapp = formatPhoneNumber(
        digitsOnly,
        formik?.values?.phoneCountry
      );
      setWhatsappInput(formattedWhatsapp);
      formik.setFieldValue("whatsappCountry", formik?.values?.phoneCountry);
      formik.setFieldValue("whatsappNumber", digitsOnly);
      if (digitsOnly.length >= 7) {
        verifyWhatsappOnChange(digitsOnly, formik?.values?.phoneCountry?.dialCode);
      }
    } else {
      const defaultCountry =
        transformedCountryOptions?.find(
          (country) =>
            country?.dialCode === formik?.values?.phoneCountry?.dialCode
        ) || transformedCountryOptions[0];
      setWhatsappInput("");
      formik.setFieldValue("whatsappCountry", defaultCountry);
      formik.setFieldValue("whatsappNumber", "");
      setWhatsappVerification({ status: "idle" });
      lastVerifiedWhatsapp.current = "";
    }
  };

  const handleDrawerClose = () => {
    formik.resetForm();
    setPhoneInput("");
    setWhatsappInput("");
    setEmailVerification({ status: "idle" });
    setPhoneVerification({ status: "idle" });
    setWhatsappVerification({ status: "idle" });
    lastVerifiedEmail.current = "";
    lastVerifiedPhone.current = "";
    lastVerifiedWhatsapp.current = "";
    handleCloseDrawer();
  };

  const destinationCountryOptions = useMemo(() => {
    if (!countryData) return [];

    return countryData.map((country: Country) => ({
      value: country.id.toString(),
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {country.name}
          </span>
        </div>
      ),
      label: country.name,
    }));
  }, [countryData]);

  const destinationCityOptions = useMemo(() => {
    if (!cityData) return [];

    return cityData.map((city: City) => ({
      value: city.id.toString(),
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {city.name}
          </span>
        </div>
      ),
      label: city.name,
    }));
  }, [cityData]);

  const universityOptions = useMemo(() => {
    if (!universityData) return [];

    return universityData.map((university: University) => ({
      value: university.id.toString(),
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {university.name}
          </span>
        </div>
      ),
      label: university.name,
    }));
  }, [universityData]);

  const roomTypeOptionsList = useMemo(() => {
    if (!roomTypeOptions) return [];

    return roomTypeOptions.map((roomType) => ({
      value: roomType.value,
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          {roomType.label}
        </span>
      ),
      selectedOptionContent: (
        <span className="capitalize text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
          {roomType.label}
        </span>
      ),
      label: roomType.label,
    }));
  }, []);

  const levelOfStudyOptionsList = useMemo(() => {
    const filteredOptions = getFilteredLevelOfStudyOptions(
      formik.values.tenantType
    );
    return filteredOptions.map((levelOfStudy) => ({
      value: levelOfStudy.value,
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {levelOfStudy.label}
          </span>
        </div>
      ),
      label: levelOfStudy.label,
    }));
  }, [formik.values.tenantType]);

  const { options: tenantTypeOptions } = useTenantTypeOptions();

  const tenantTypeOptionsList = useMemo(() => {
    return tenantTypeOptions.map((tenantType) => ({
      value: tenantType.value,
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {tenantType.label}
          </span>
        </div>
      ),
      label: tenantType.label,
    }));
  }, [tenantTypeOptions]);

  const utmSourceOptions = useMemo(() => {
    if (!utmSourceData) return [];

    const options = utmSourceData.map((utmSource: UtmSource) => ({
      value: utmSource.utm_source,
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {utmSource.utm_source}
          </span>
        </div>
      ),
      label: utmSource.utm_source,
    }));

    // If the current value is not in the API options, add it so it displays as selected
    const currentValue = formik.values.utmSource;
    if (
      currentValue &&
      !options.some((opt: DropdownOption) => opt.value === currentValue)
    ) {
      options.unshift({
        value: currentValue,
        content: (
          <div className="flex items-center">
            <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
              {currentValue}
            </span>
          </div>
        ),
        label: currentValue,
      });
    }

    return options;
  }, [utmSourceData, formik.values.utmSource]);

  const utmMediumOptions = useMemo(() => {
    if (!utmMediumData) return [];

    const options = utmMediumData.map((item: UtmMedium) => ({
      value: item.utm_medium,
      content: (
        <div className="flex items-center">
          <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
            {item.utm_medium}
          </span>
        </div>
      ),
      label: item.utm_medium,
    }));

    const currentValue = formik.values.utmMedium;
    if (
      currentValue &&
      !options.some((opt: DropdownOption) => opt.value === currentValue)
    ) {
      options.unshift({
        value: currentValue,
        content: (
          <div className="flex items-center">
            <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
              {currentValue}
            </span>
          </div>
        ),
        label: currentValue,
      });
    }

    return options;
  }, [utmMediumData, formik.values.utmMedium]);

  const choosenPropsOptions = useMemo(() => {
    const options = (choosenPropsData?.options || []).map(
      (item: { label: string; value: number }) => ({
        value: item.value.toString(),
        content: (
          <span className="text-md text-heading-text dark:text-heading-text-dk">
            {item.label}
          </span>
        ),
        selectedOptionContent: (
          <span className="capitalize text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
            {item.label}
          </span>
        ),
        label: item.label,
      })
    );

    const existingProps =
      leadDetails?.choosen_props || leadDetails?.chosen_props;
    if (existingProps) {
      existingProps.forEach((prop) => {
        const propId = prop.property_id.toString();
        if (!options.some((opt: DropdownOption) => opt.value === propId)) {
          options.push({
            value: propId,
            content: (
              <span className="text-md text-heading-text dark:text-heading-text-dk">
                {prop.property_name}
              </span>
            ),
            selectedOptionContent: (
              <span className="capitalize text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {prop.property_name}
              </span>
            ),
            label: prop.property_name,
          });
        }
      });
    }

    // The client-default property may fall outside the fetched properties page,
    // so keep it selectable for the create-lead prefill.
    const defaultPropId = leadCreateDefaults?.property_id;
    if (
      defaultPropId &&
      !options?.some(
        (opt: DropdownOption) => opt?.value === defaultPropId?.toString()
      )
    ) {
      const defaultPropName =
        leadCreateDefaults?.property_name || defaultPropId?.toString();
      options.push({
        value: defaultPropId?.toString(),
        content: (
          <span className="text-md text-heading-text dark:text-heading-text-dk">
            {defaultPropName}
          </span>
        ),
        selectedOptionContent: (
          <span className="capitalize text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
            {defaultPropName}
          </span>
        ),
        label: defaultPropName,
      });
    }

    return options;
  }, [
    choosenPropsData,
    leadDetails?.choosen_props,
    leadDetails?.chosen_props,
    leadCreateDefaults,
  ]);

  const leadDetailTagsOptions = useMemo(() => {
    if (!leadDetailTagsData) return [];

    return leadDetailTagsData.map((leadDetailTag: LeadDetailTag) => {
      const displayName = getLeadTagDisplayName(
        leadDetailTag.name,
        client?.client_code
      );
      return {
        value: leadDetailTag.code,
        content: (
          <span
            className={`rounded-[4px] px-[4px] py-[2px] capitalize text-md font-normal text-verba-indigo dark:text-verba-indigo-dk ${getTagBgColor(
              leadDetailTag.colour
            )}`}
          >
            {displayName}
          </span>
        ),
        selectedOptionContent: (
          <span
            className={`capitalize text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk`}
          >
            {displayName}
          </span>
        ),
        label: displayName,
        bgColor: getTagBgColor(leadDetailTag.colour),
      };
    });
  }, [leadDetailTagsData, client?.client_code]);

  const currentStageOptions = useMemo(() => {
    if (!stageFieldOptionsData?.options) return [];
    return stageFieldOptionsData.options.map(
      (stage: { label: string; value: string }) => ({
        value: stage.value,
        content: (
          <div className="flex items-center">
            <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
              {stage.label}
            </span>
          </div>
        ),
        label: stage.label,
      })
    );
  }, [stageFieldOptionsData]);

  const assignedAgentOptions: DropdownOption[] = useMemo(() => {
    return (allAgentsData?.items || []).map(
      (agent: { _id: string; name: string }) => ({
        value: agent._id,
        label: agent.name,
        content: (
          <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
            {agent.name}
          </span>
        ),
      })
    );
  }, [allAgentsData]);

  // Helper functions for DynamicFieldRenderer
  const getOptions = (fieldKey: string): DropdownOption[] => {
    const optionsMap: { [key: string]: DropdownOption[] } = {
      "demographics.destination_country": destinationCountryOptions,
      "demographics.destination_city": destinationCityOptions,
      "demographics.university": universityOptions,
      "demographics.source_country": destinationCountryOptions,
      room_type: roomTypeOptionsList,
      level_of_study: levelOfStudyOptionsList,
      tenant_type: tenantTypeOptionsList,
      utm_source: utmSourceOptions,
      utm_medium: utmMediumOptions,
      choosen_props: choosenPropsOptions,
      tags: leadDetailTagsOptions,
      current_stage: currentStageOptions,
      assigned_agents: assignedAgentOptions,
    };

    return optionsMap[fieldKey] || [];
  };

  const getLoadingState = (fieldKey: string): boolean => {
    const loadingMap: { [key: string]: boolean } = {
      "demographics.destination_country": isCountryLoading,
      "demographics.destination_city": isCityLoading,
      "demographics.university": isUniversityLoading,
      "demographics.source_country": isCountryLoading,
      utm_source: isUtmSourceLoading,
      utm_medium: isUtmMediumLoading,
      choosen_props: isChoosenPropsLoading,
      tags: isLeadDetailTagsLoading,
      current_stage: isStageOptionsLoading,
      assigned_agents: isAgentsLoading,
    };

    return loadingMap[fieldKey] || false;
  };

  const getPhoneHandlers = () => ({
    phoneInput,
    whatsappInput,
    sortedCountryOptions,
    handlePhoneCountryChange,
    handleWhatsappCountryChange,
    handlePhoneInputChange,
    handleWhatsappInputChange,
    handleSameAsPhoneNumberChange,
  });

  const getBudgetConfig = () => ({
    leadCurrencyUnit,
    leadDurationUnit,
    clientRegion: client?.region,
  });

  const hasPhoneField = useMemo(
    () => sortedFields.some((field) => field.field_key === "phone"),
    [sortedFields]
  );

  useEffect(() => {
    if (leadDetails && openDrawer) {
      const phoneNumber = leadDetails.phone || "";
      const whatsappNumber = leadDetails.whatsapp_phone || "";

      if (phoneNumber && formik.values.phoneCountry) {
        const formattedPhone = formatPhoneNumber(
          phoneNumber,
          formik.values.phoneCountry
        );
        setPhoneInput(formattedPhone);
      }

      if (whatsappNumber && formik.values.whatsappCountry) {
        const formattedWhatsapp = formatPhoneNumber(
          whatsappNumber,
          formik.values.whatsappCountry
        );
        setWhatsappInput(formattedWhatsapp);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails, openDrawer]);

  useEffect(() => {
    if (formik?.values?.isSameAsPhone) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedWhatsapp = formatPhoneNumber(
        digitsOnly,
        formik?.values?.phoneCountry
      );
      setWhatsappInput(formattedWhatsapp);
      formik.setFieldValue("whatsappCountry", formik?.values?.phoneCountry);
      formik.setFieldValue("whatsappNumber", digitsOnly);
      setWhatsappVerification({ status: "idle" });
      lastVerifiedWhatsapp.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.phoneCountry, phoneInput]);

  // Apply the client's create-lead defaults once per drawer open, and only for
  // create — an edit must never overwrite the lead's own values.
  useEffect(() => {
    if (!openDrawer) {
      hasAppliedCreateDefaultsRef.current = false;
      return;
    }
    if (isEdit || hasAppliedCreateDefaultsRef.current || !leadCreateDefaults) {
      return;
    }

    const { country_id, city_id, property_id } = leadCreateDefaults;
    if (!country_id && !city_id && !property_id) return;

    hasAppliedCreateDefaultsRef.current = true;
    if (country_id) {
      formik.setFieldValue("destinationCountry", country_id.toString());
    }
    if (city_id) {
      formik.setFieldValue("destinationCity", city_id.toString());
    }
    if (property_id) {
      formik.setFieldValue("choosenProps", [property_id.toString()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer, isEdit, leadCreateDefaults]);

  // Re-validate when the drawer opens, the lead loads (edit prefill), or the
  // field config/schema arrives — so `isValid` (and the Save button) reflects
  // the current values without waiting for the user to touch a field.
  useEffect(() => {
    if (openDrawer) {
      formik.validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer, leadDetails, validationSchema]);

  return (
    <Drawer
      open={openDrawer}
      onClose={handleDrawerClose}
      direction="right"
      size={size}
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-[18px] py-[12px] shrink-0">
            <div className="flex items-center gap-2">
              {isEdit ? (
                <EditUserIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
              ) : (
                <AddUserIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
              )}
              <span className="text-heading-text dark:text-heading-text-dk font-semibold text-md">
                {isEdit ? "Edit Lead Details" : "Create New Lead"}
              </span>
            </div>
            <button
              className={`w-[16px] h-[16px] bg-tag-gray dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110`}
              aria-label="Close modal"
              onClick={handleDrawerClose}
            >
              <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
            </button>
          </div>

          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-col flex-1 min-h-0"
          >
            <div
              className={`flex-1 px-[18px] py-[12px] overflow-y-auto min-h-0 ${visibleScrollbar(
                "[&::-webkit-scrollbar]:w-[8px]"
              )}`}
            >
              <div className="flex flex-col gap-[16px]">
                {sortedFields.map((field) => (
                  <DynamicFieldRenderer
                    key={field.field_key}
                    field={field}
                    formik={formik}
                    getOptions={getOptions}
                    getLoadingState={getLoadingState}
                    getPhoneHandlers={getPhoneHandlers}
                    getBudgetConfig={getBudgetConfig}
                    hasPhoneField={hasPhoneField}
                    verification={{
                      emailVerification,
                      phoneVerification,
                      whatsappVerification,
                      setEmailVerification,
                      setPhoneVerification,
                      setWhatsappVerification,
                      verifyEmailOnChange,
                      verifyPhoneOnChange,
                      verifyWhatsappOnChange,
                      handleUseDifferentField,
                      handleViewLead,
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-[8px] px-[18px] py-[12px] border-t border-border-gray dark:border-border-gray-dk shrink-0">
              <Button
                type="button"
                onClick={handleDrawerClose}
                innerText="Cancel"
                buttonContainerClass="rounded-[8px] py-[4px] w-full bg-transparent dark:bg-transparent-dk border border-verba-indigo dark:border-verba-indigo-dk"
                innerTextClass="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk"
              />
              <Button
                type="submit"
                innerText="Save"
                buttonContainerClass={`rounded-[8px] py-[4px] w-full bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk ${
                  isUpdating || !formik.isValid
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
                isLoading={isUpdating}
                disabled={isUpdating || !formik.isValid}
              />
            </div>
          </form>
        </div>
      </Drawer>
  );
};

export default DynamicLeadCreateEditForm;
